import React, { useEffect, useRef, useState } from "react";
import "./VersionUploadPopup.css";
import { useTranslation } from "react-i18next";
import {
  fetchNotifications,
  fetchProjectById,
  readAllNotifications,
  setCurrentUploadingVideosInfo,
} from "../../store/appSlice";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";
import {
  currentUploadingVideosSelector,
  notificationsSelector,
} from "../../store/selectors";
import bellIcon from "../../assets/bell.svg";
import * as tus from "tus-js-client";
import Avatar from "../Avatar/Avatar";
import LinearProgress from "@mui/material/LinearProgress";
import { useNavigate } from "react-router";
import {
  fetchVersion,
  forceDeleteVersion,
  updateVersion,
} from "../../store/projectsSlice";

const VersionUploadPopup = ({}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUploadingVideosInfo = useSelector(
    currentUploadingVideosSelector
  );
  const videosInfoRef = useRef([]);
  const [currentUploadingVideoIds, setCurrentUploadingVideoIds] = useState([]);
  const [videosUploadData, setVideosUploadData] = useState({});
  const checkingTimeout = useRef();

  useEffect(() => {
    videosInfoRef.current = currentUploadingVideosInfo;
  }, [currentUploadingVideosInfo]);

  useEffect(() => {
    if (currentUploadingVideosInfo.length) {
      const newVideoIndex = currentUploadingVideosInfo.findIndex(
        (vid) => !videosUploadData[vid.data.id] && !vid.isUploaded
      );
      let newVideo = null;
      if (newVideoIndex > -1) {
        newVideo = currentUploadingVideosInfo[newVideoIndex];
      }
      if (!newVideo) return;
      setVideosUploadData({
        ...videosUploadData,
        [newVideo.data.id]: {
          id: newVideo.data.id,
        },
      });
    }
  }, [currentUploadingVideosInfo.length]);

  const updateVersionsStatus = (uploadingVideosArray = []) => {
    clearTimeout(checkingTimeout.current);

    checkingTimeout.current = setTimeout(async () => {
      let newUploadingVideosArray = [...videosInfoRef.current];
      const filteredProcessingVideos = newUploadingVideosArray.filter(
        (videoInfo) => {
          const isEmpty =
            videoInfo.data.videoStatus === "empty" ||
            !videoInfo.data.videoStatus;
          const isPending = videoInfo.data.videoStatus === "pending";
          const isFinal = videoInfo.data.status === "FINAL";

          if (isPending || (isEmpty && isFinal)) return true;
          return false;
        }
      );
      if (!filteredProcessingVideos.length) {
        return;
      }

      let updatedCurrentUploadingArray = [...newUploadingVideosArray];

      for (let i = 0; i < filteredProcessingVideos.length; i++) {
        const videoToUpdate = filteredProcessingVideos[i];
        const updatedVersionRes = await dispatch(
          fetchVersion(videoToUpdate.data.id)
        );
        let vidIndex = updatedCurrentUploadingArray.findIndex(
          (vid) => vid.data.id === videoToUpdate.data.id
        );
        let updatedVid = {
          ...updatedCurrentUploadingArray[vidIndex],
          data: updatedVersionRes.payload,
          isCompleted: updatedVersionRes.payload.videoStatus === "ready",
        };
        if (
          updatedVersionRes.payload.videoStatus === "ready" &&
          window.location.href.includes(
            `projects/${updatedVersionRes.payload.projectId}`
          )
        ) {
          fetchCurrentProjectById(updatedVersionRes.payload.projectId);
        }
        updatedCurrentUploadingArray[vidIndex] = updatedVid;
      }

      dispatch(setCurrentUploadingVideosInfo(updatedCurrentUploadingArray));
      updateVersionsStatus();
    }, 3000);
  };

  const fetchCurrentProjectById = (projId) => {
    dispatch(fetchProjectById(projId));
  };
  if (!currentUploadingVideosInfo.length) return null;

  return currentUploadingVideosInfo.map((videoInfo, index) => (
    <VideoUploadData
      currentUploadingVideosInfo={videosInfoRef.current}
      currentUploadingVideosInfoArray={currentUploadingVideosInfo}
      uploadingVideoInfo={videoInfo}
      infoIndex={index}
      videoUploadDataArray={Object.values(videosUploadData)}
      videosUploadData={videosUploadData}
      fetchCurrentProjectById={fetchCurrentProjectById}
      updateVersionsStatus={updateVersionsStatus}
    />
  ));
};

const VideoUploadData = ({
  videoUploadDataArray,
  videosUploadData,
  currentUploadingVideosInfoArray,
  currentUploadingVideosInfo,
  uploadingVideoInfo,
  infoIndex,
  fetchCurrentProjectById,
  updateVersionsStatus,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [uploadStarted, setUploadStarted] = useState(false);
  const [currentMegabytes, setCurrentMegabytes] = useState(0);
  const [totalMegabytes, setTotalMegabytes] = useState(0);
  const componentVideosInfoRef = useRef([]);
  const [currentUploadObject, setCurrentUploadObject] = useState(null);
  const nodeRef = useRef(null);

  useEffect(() => {
    componentVideosInfoRef.current = currentUploadingVideosInfo;
  }, [currentUploadingVideosInfo]);

  useEffect(() => {
    if (uploadStarted) return;
    setUploadStarted(true);
    let newVideo = uploadingVideoInfo;
    let uploads = {};
    const { data, file } = newVideo;
    let upload_url = "https://" + "vod-uploader-2.edgevideo.ru" + "/upload/";

    uploads[data.video.id] = new tus.Upload(file, {
      endpoint: upload_url,
      metadata: {
        filename: data.video.name,
        client_id: data.video.client_id,
        video_id: data.video.id,
        token: data.token,
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        setCurrentMegabytes(bytesUploaded);
        setTotalMegabytes(bytesTotal);
      },
      onSuccess: () => {
        dispatch(
          updateVersion({
            versionId: data.id,
            title: file.name,
            status: "FINAL",
          })
        ).then(async (res) => {
          if (!res.error) {
            if (
              window.location.href.includes(
                `projects/${newVideo.parentProjectId}`
              )
            ) {
              fetchCurrentProjectById(newVideo.parentProjectId);
            }
            let newUploadingVideosArray = [...componentVideosInfoRef.current];
            const newVideoIndex = newUploadingVideosArray.findIndex(
              (vid) => vid.data.id === newVideo.data.id
            );
            newUploadingVideosArray[newVideoIndex] = {
              ...newVideo,
              data: res.payload,
              isUploaded: true,
            };
            dispatch(setCurrentUploadingVideosInfo(newUploadingVideosArray));
            updateVersionsStatus();
          }
        });
      },
    });
    uploads[data.video.id].findPreviousUploads().then((previousUploads) => {
      // previousUploads is an array containing details about the previously started uploads.
      // The objects in the array have following properties:
      // - size: The upload's size in bytes
      // - metadata: The metadata associated with the upload during its creation
      // - creationTime: The timestamp when the upload was created

      // We ask the end user if they want to resume one of those uploads or start a new one.
      // var chosenUpload = askToResumeUpload(previousUploads);

      // console.log(previousUploads, uploadingVideoInfo);

      const videoToResumeUpload = previousUploads.find(
        (upl) => upl.metadata.video_id === newVideo.data.video.id
      );

      // If an upload has been chosen to be resumed, instruct the upload object to do so.
      if (videoToResumeUpload) {
        // console.log("RESUMING UPLOAD! ", videoToResumeUpload);
        uploads[data.video.id].resumeFromPreviousUpload(videoToResumeUpload);
      }

      setCurrentUploadObject(uploads[data.video.id]);

      uploads[data.video.id].start();
    });
  }, [videoUploadDataArray.length]);

  console.log(currentUploadObject);

  const deleteVideoFromUploaded = (videoId) => {
    const vidIndex = currentUploadingVideosInfo.findIndex(
      (vid) => vid.data.id === videoId
    );
    const newUploadingVideosArray = [...currentUploadingVideosInfo];
    newUploadingVideosArray.splice(vidIndex, 1);
    dispatch(setCurrentUploadingVideosInfo(newUploadingVideosArray));
  };

  let bottomOffset = 32;
  if (infoIndex != 0) {
    bottomOffset = 32 + infoIndex * 128.5 + 8;
  }

  const handleAbortUploading = (isAtProcessingStage = false) => {
    const message = isAtProcessingStage
      ? "Вы действительно хотите прервать обработку видео и удалить его?"
      : "Вы действительно хотите прервать загрузку видео?";
    if (confirm(message)) {
      if (!isAtProcessingStage) {
        currentUploadObject.abort(true);
      }

      const vidIndex = currentUploadingVideosInfo.findIndex(
        (vid) => vid.data.id === uploadingVideoInfo.data.id
      );
      const newUploadingVideosArray = [...currentUploadingVideosInfo];
      const deletedVideo = newUploadingVideosArray[vidIndex];
      newUploadingVideosArray.splice(vidIndex, 1);
      dispatch(setCurrentUploadingVideosInfo(newUploadingVideosArray));

      dispatch(forceDeleteVersion(uploadingVideoInfo.data.id)).then((res) => {
        if (
          window.location.href.includes(
            `projects/${deletedVideo.parentProjectId}`
          )
        ) {
          fetchCurrentProjectById(deletedVideo.parentProjectId);
        }
      });
    }

    return;
  };

  return (
    <Draggable nodeRef={nodeRef}>
      <span
        style={{ position: "absolute", bottom: 0, right: 0, zIndex: 10000000 }}
        ref={nodeRef}
      >
        <div
          className="version-upload-container"
          style={{ bottom: bottomOffset }}
        >
          {true && (
            <button
              className="version-upload-close-button"
              onClick={() => {
                if (!uploadingVideoInfo?.isUploaded) {
                  handleAbortUploading();
                  return;
                }
                if (
                  uploadingVideoInfo?.isUploaded &&
                  !uploadingVideoInfo?.isCompleted
                ) {
                  handleAbortUploading(true);
                  return;
                }
                const vidIndex = currentUploadingVideosInfo.findIndex(
                  (vid) => vid.data.id === uploadingVideoInfo.data.id
                );
                const newUploadingVideosArray = [...currentUploadingVideosInfo];
                newUploadingVideosArray.splice(vidIndex, 1);
                dispatch(
                  setCurrentUploadingVideosInfo(newUploadingVideosArray)
                );
              }}
            />
          )}
          <span>{uploadingVideoInfo?.file?.name}</span>
          {!uploadingVideoInfo?.isUploaded &&
            !uploadingVideoInfo?.isCompleted && (
              <LinearProgress
                variant="determinate"
                style={{ marginTop: 12 }}
                value={
                  totalMegabytes ? 100 * (currentMegabytes / totalMegabytes) : 0
                }
                sx={{
                  backgroundColor: "#5CB1F6",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#427FFF",
                  },
                }}
              />
            )}
          {uploadingVideoInfo?.isUploaded &&
            !uploadingVideoInfo?.isCompleted && (
              <LinearProgress style={{ marginTop: 12 }} color="secondary" />
            )}

          {uploadingVideoInfo?.isCompleted ? (
            <>
              <span style={{ color: "#717B8E", fontSize: 14, marginTop: 10 }}>
                Версия готова к работе
              </span>
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#e8f2ff",
                  borderRadius: "80px",
                  border: "none",
                  color: "#1f242e",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  marginTop: 15,
                  alignSelf: "flex-start",
                }}
                onClick={() => {
                  const vidIndex = currentUploadingVideosInfo.findIndex(
                    (vid) => vid.data.id === uploadingVideoInfo.data.id
                  );
                  const newUploadingVideosArray = [
                    ...currentUploadingVideosInfo,
                  ];
                  newUploadingVideosArray.splice(vidIndex, 1);
                  dispatch(
                    setCurrentUploadingVideosInfo(newUploadingVideosArray)
                  );
                  navigate(`/video/${uploadingVideoInfo.data.id}`);
                }}
              >
                Перейти
              </div>
            </>
          ) : uploadingVideoInfo?.isUploaded ? (
            <span style={{ color: "#717B8E", fontSize: 14, marginTop: 10 }}>
              Версия обрабатывается...
            </span>
          ) : totalMegabytes ? (
            <span style={{ color: "#717B8E", fontSize: 14, marginTop: 10 }}>
              {(currentMegabytes / 1024 / 1024).toFixed(2)}
              МБ / {(totalMegabytes / 1024 / 1024).toFixed(2)}
              МБ
            </span>
          ) : (
            <span style={{ color: "#717B8E", fontSize: 14, marginTop: 10 }}>
              Подготовка...
            </span>
          )}
        </div>
      </span>
    </Draggable>
  );
};

export default VersionUploadPopup;
